/* eslint-disable react/jsx-props-no-spreading */

import {
  Button,
  ButtonGroup,
  Center,
  Input,
  InputGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';

import SolidButton from '@/client/components/shared/buttons/SolidButton';
import { Workflow } from '@/client/services/api/graphql/gql/graphql';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useGetWorkflowDetails } from '../hooks/useGetWorkflowDetails';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';
import { useUpdateWorkflowName } from '../hooks/useUpdateWorkflowName';

interface AssignmentEditNameModalProps {
  id: string;
  isOpen: boolean;
  onClose: () => void;
  refetchAction: () => void;
}

export default function AssignmentEditNameModal({
  id,
  isOpen,
  onClose,
  refetchAction,
}: AssignmentEditNameModalProps) {
  const { t } = useTranslation();
  const { setToast } = useToastStore();
  const { updateWorkflowNameMutation } = useUpdateWorkflowName();

  const { data: workflowData, isLoading, isError } = useGetWorkflowDetails(id);

  type WorkflowNameFormData = {
    name: string;
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { isValid, errors },
  } = useForm<WorkflowNameFormData>({
    defaultValues: {
      name: workflowData?.settings.name,
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (workflowData) {
      reset({
        name: workflowData.settings.name,
      });
    }
  }, [workflowData]);

  const hasNameError = !!errors?.name;

  const onSubmit = (formData: WorkflowNameFormData, initialWorkflowData: Workflow) => {
    const payload = {
      id,
      data: initialWorkflowData,
      updatedName: formData.name,
      refetchAction,
      onClose,
    };

    return updateWorkflowNameMutation.mutateAsync(payload);
  };

  if (isError) {
    setToast({
      show: true,
      status: 'error',
      title: t('admin.assignments.details.edit.workflowName.loadingError'),
    });

    onClose();

    return;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />

      <ModalContent>
        {isLoading && (
          <ModalBody>
            <Center>
              <Spinner />
            </Center>
          </ModalBody>
        )}

        {!isLoading && !isError && (
          <form onSubmit={handleSubmit((formData) => onSubmit(formData, workflowData))}>
            <ModalBody paddingTop={12} paddingX={10}>
              <Stack spacing={4} marginBottom={10}>
                <Text variant="createLabel">
                  {t('admin.assignments.details.edit.workflowName')}
                </Text>

                <InputGroup>
                  <Input
                    {...register('name', {
                      required: true,
                      minLength: 1,
                      validate: (value) => value.length > 0 && value !== workflowData.settings.name,
                    })}
                    autoFocus
                    placeholder={`${t('admin.assignments.details.edit.workflowName.placeholder')}`}
                    isInvalid={hasNameError}
                    variant="create"
                  />
                </InputGroup>

                {hasNameError && (
                  <Text variant="error">
                    {t('admin.assignments.details.edit.workflowName.error')}
                  </Text>
                )}
              </Stack>
            </ModalBody>

            <ModalFooter>
              <ButtonGroup spacing={2} justifyContent="center" width="full">
                <Button
                  variant="createCancel"
                  onClick={() => {
                    reset();
                    onClose();
                  }}
                >
                  {t('global.actions.cancel')}
                </Button>

                <SolidButton
                  type="submit"
                  variant="solid"
                  isLoading={updateWorkflowNameMutation.isLoading}
                  isDisabled={!isValid}
                >
                  {t('global.actions.save')}
                </SolidButton>
              </ButtonGroup>
            </ModalFooter>
          </form>
        )}
      </ModalContent>
    </Modal>
  );
}
